import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { URLs } from '../../../../constants';
import { featureFlags } from '../../../../feature-flags';
import { DemoDataBanner } from '../../../demo-data-banner';
import { customROITimePeriods } from '../../constants';
import { getDateRangeText } from '../../utils/date-range-text';
import { NSRRoiBenchmarkTimelineChart, NSRRoiPatientsTypeTimelineChart } from '../charts';
import { Filters, NSRSummary } from '../components';
import { useNSRRoiShallowStore } from '../hooks';

export const NSRRoiDashboard = () => {
  const { t } = useTranslation('analytics');
  const [loadingCharts, setLoadingCharts] = useState<Record<string, boolean | undefined>>({});
  const {
    filterHintText: subtitle,
    filters,
    setDefinitionsHelperDateRange,
  } = useNSRRoiShallowStore('filterHintText', 'filters', 'setDefinitionsHelperDateRange');

  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);

  const handleUpdateIsLoading = useCallback((key: string, isLoading?: boolean) => {
    setLoadingCharts((prevState) => ({
      ...prevState,
      [key]: isLoading,
    }));
  }, []);

  const isLoading = useMemo(() => {
    return Object.values(loadingCharts).some((loading) => loading);
  }, [loadingCharts]);

  useEffect(() => {
    setDefinitionsHelperDateRange(
      getDateRangeText({
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        timePeriods: customROITimePeriods,
      })
    );
  }, [filters?.startDate, filters?.endDate, setDefinitionsHelperDateRange]);

  return (
    <>
      {isDemoAccount && (
        <DemoDataBanner title={t("You're taking No-Show Rate Conversion for a test drive with demo data!")} />
      )}
      <Page>
        <Page.Header>
          <Page.Header.Breadcrumbs
            breadcrumbs={[
              {
                label: t('Analytics'),
                to: URLs.ANALYTICS_DASHBOARD,
              },
              {
                label: t('No-Show Rate'),
              },
            ]}
          />
          <Page.Header.Heading>
            <Page.Header.Title title={t('No-Show Rate')} />
            <Page.Header.Subtitle subtitle={t('Showing results for {{subtitle}}', { subtitle })} />
          </Page.Header.Heading>
        </Page.Header>

        <Page.Body css={styles.body}>
          <div css={styles.body}>
            <Filters isLoading={isLoading} />
            <NSRSummary />
            {/* TODO :: uncomment it when multi is ready to land */}
            {/* {(filters?.locationIds?.length || 0) > 1 ? (
              <>
                <NSRRoiLocationsComparisonPatientType onLoading={handleUpdateIsLoading} />
              </>
            ) : ( */}
            <>
              <NSRRoiBenchmarkTimelineChart onLoading={handleUpdateIsLoading} />
              <NSRRoiPatientsTypeTimelineChart onLoading={handleUpdateIsLoading} />
            </>
            {/* )} */}
          </div>
        </Page.Body>
      </Page>
    </>
  );
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
  `,
};
